import React from "react";
import { IconArrowRight } from "@tabler/icons-react";
import clsx from "clsx";
import Button from "components/Button";
import CircularProgressBar from "components/CircularProgressBar";
import { useTranslation } from "react-i18next";
import { getPercentageStatusColor } from "utils";
import { useNavigate } from "react-router";
import { buildingStore } from "store/Building/BuildingStore";
import { ROUTES_ENUM } from "routes/consts";

export default function MappingStatusChart({
  percentage,
  certification,
  onClose,
  compliantCount,
  totalCount,
}: {
  percentage: number;
  certification: string;
  onClose: () => void;
  compliantCount?: number;
  totalCount?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openObjectFiles = () => {
    navigate(
      `../buildings/${buildingStore.currentBuilding?.id}/${ROUTES_ENUM.SOURCE_FILES}`
    );
    onClose();
  };

  const getStatusQuality = () => {
    if (percentage >= 75) {
      return "high";
    } else if (percentage >= 25) {
      return "medium";
    } else {
      return "low";
    }
  };

  const getStrokeColor = (percentage: number) => {
    if (percentage >= 75) {
      return "#DCFCE7";
    } else if (percentage >= 25) {
      return "#FEF9C3";
    } else {
      return "#FEE2E2";
    }
  };

  return (
    <div
      className={clsx(
        "flex border p-4 rounded-md w-full items-center flex-col justify-between",
        {
          "border-red-100 bg-red-50": getStatusQuality() === "low",
          "border-yellow-100 bg-yellow-50": getStatusQuality() === "medium",
          "border-green-100 bg-green-50": getStatusQuality() === "high",
        }
      )}
    >
      <div className="flex items-center flex-col">
        <div className="text-lg font-semibold text-gray-800 mb-4">
          {getStatusQuality() === "high"
            ? t("certExport.objectsMapped")
            : t("certExport.fewObjectsMapped")}
        </div>
        <div className="flex items-center mb-4">
          <CircularProgressBar
            size={41}
            strokeWidth={8}
            textStyle="!text-sm font-semibold text-gray-700 !mt-0 mr-0 ml-1"
            activeStroke={getPercentageStatusColor(percentage)}
            strokeColor={getStrokeColor(percentage)}
            percent={percentage}
            className="!w-[80px] !h-[80px] flex items-center"
          />
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: t("certExport.objectsMappedDesc", {
              num_compliant_objects: compliantCount ?? "",
              num_all_objects: totalCount ?? "",
              certification: certification,
            }),
          }}
        ></div>
      </div>
      <Button
        onClick={openObjectFiles}
        className={clsx("border-none text-base mt-3", {
          "text-red-700": getStatusQuality() === "low",
          "text-yellow-700": getStatusQuality() === "medium",
          hidden: getStatusQuality() === "high",
        })}
        type="link"
        width="fit-content"
        trailingIcon={<IconArrowRight />}
      >
        {t("certExport.openObjectFiles")}
      </Button>
    </div>
  );
}
